import { useState, useMemo } from 'react';
import { PolicyWithStatus, PolicyStatus } from '@/types/insurance';
import { InsurancePolicyCard } from './InsurancePolicyCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface InsurancePolicyListProps {
  policies: PolicyWithStatus[];
  onEdit?: (policy: PolicyWithStatus) => void;
  onDelete?: (policyId: string) => void;
}

type SortField = 'premium_amount' | 'coverage_amount' | 'end_date';
type SortOrder = 'asc' | 'desc';

export function InsurancePolicyList({ policies, onEdit, onDelete }: InsurancePolicyListProps) {
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('end_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const filteredAndSortedPolicies = useMemo(() => {
    let result = [...policies];

    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(p => p.policy_type === filterType);
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(p => p.status === filterStatus);
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      switch (sortField) {
        case 'premium_amount':
          aValue = a.premium_amount;
          bValue = b.premium_amount;
          break;
        case 'coverage_amount':
          aValue = a.coverage_amount;
          bValue = b.coverage_amount;
          break;
        case 'end_date':
          aValue = new Date(a.end_date).getTime();
          bValue = new Date(b.end_date).getTime();
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return result;
  }, [policies, filterType, filterStatus, sortField, sortOrder]);

  if (policies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-lg font-medium text-muted-foreground">No insurance policies found</p>
        <p className="text-sm text-muted-foreground mt-2">
          Create your first policy to start tracking your insurance coverage
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Sorting Controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label htmlFor="filter-type">Filter by Type</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger id="filter-type">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="health">Health</SelectItem>
              <SelectItem value="life">Life</SelectItem>
              <SelectItem value="auto">Auto</SelectItem>
              <SelectItem value="home">Home</SelectItem>
              <SelectItem value="travel">Travel</SelectItem>
              <SelectItem value="disability">Disability</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="filter-status">Filter by Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger id="filter-status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="renewal_due">Renewal Due</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sort-field">Sort by</Label>
          <Select value={sortField} onValueChange={(value) => setSortField(value as SortField)}>
            <SelectTrigger id="sort-field">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="end_date">Expiration Date</SelectItem>
              <SelectItem value="premium_amount">Premium Amount</SelectItem>
              <SelectItem value="coverage_amount">Coverage Amount</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sort-order">Order</Label>
          <Select value={sortOrder} onValueChange={(value) => setSortOrder(value as SortOrder)}>
            <SelectTrigger id="sort-order">
              <SelectValue placeholder="Order" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Ascending</SelectItem>
              <SelectItem value="desc">Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Policy Cards */}
      {filteredAndSortedPolicies.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-lg font-medium text-muted-foreground">No policies match your filters</p>
          <p className="text-sm text-muted-foreground mt-2">
            Try adjusting your filter criteria
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filteredAndSortedPolicies.map((policy) => (
            <InsurancePolicyCard
              key={policy.id}
              policy={policy}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
